import React from 'react';

const SEVEN = [
  ['Discovered', 'known'], ['Identified', 'spiffe'], ['Intent', 'checked'],
  ['Access', 'checked'], ['Data', 'guarded'], ['Trust', 'scored'], ['Sealed', 'replayable'],
];

/**
 * The moment it is protected, and what that means from now on.
 *
 * <h3>The seven are not steps somebody took</h3>
 *
 * They are what happens on every call from here. In the build sequence they
 * would read as seven more chores; here they are what the person has just
 * bought.
 *
 * <h3>Two scores, never conflated</h3>
 *
 * A design-time score can exist the moment the agent does, read off what it was
 * told and what kind of agent it is. The run-time Trust Score is earned by
 * behaving inside the boundary and cannot be shown before there are runs. When
 * there is no design-time score either, this says a score is earned — it does
 * not print a nought nobody established.
 */
export default function ProtectLive({
  name, ledger = [], pending = false, score = null, endpoint, credential, onOpen,
}) {
  const tone = pending ? 'hold' : 'allow';
  return (
    <div style={{ minWidth: 0 }}>
      <div className="p7-card"
           style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-4)',
                    padding: 'var(--p7-space-5)',
                    borderLeft: `3px solid var(--p7-${tone === 'hold' ? 'hold' : 'allow'})` }}>
        <span aria-hidden="true"
              style={{ width: '46px', height: '46px', borderRadius: '50%', flex: 'none',
                       display: 'grid', placeItems: 'center',
                       background: `var(--p7-${tone}-bg)`, color: `var(--p7-${tone}-ink)`,
                       fontSize: '20px', fontWeight: 700 }}>
          {pending ? '!' : '✓'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, font: '700 20px/26px var(--p7-font-sans)',
                       letterSpacing: '-.02em' }}>
            {pending ? `${name} is registered — not yet running.` : `${name} is protected.`}
          </h1>
          <p style={{ margin: '3px 0 0', font: 'var(--p7-text-body)',
                      fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-muted)' }}>
            {pending
              ? 'Everything below is in place. It stays inactive until the nominated owner '
                + 'accepts accountability.'
              : 'Own identity, rules bound, probe on record. Every call now runs the seven below.'}
          </p>
        </div>
        <div style={{ textAlign: 'right', flex: 'none' }}>
          {score?.score != null ? (
            <>
              <div style={{ font: '700 26px/28px var(--p7-font-sans)',
                            color: 'var(--p7-hold-ink)' }}>{score.score}</div>
              <div style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                            color: 'var(--p7-ink-subtle)' }}>
                Design-time{score.factors ? ` · ${score.factors} of 6 factors` : ''}
              </div>
            </>
          ) : (
            <>
              <div style={{ font: '600 15px/20px var(--p7-font-sans)',
                            color: 'var(--p7-ink-faint)' }}>Unrated</div>
              <div style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                            color: 'var(--p7-ink-subtle)' }}>A score is earned, not assigned</div>
            </>
          )}
        </div>
      </div>

      <div data-testid="ledger" className="p7-card"
           style={{ marginTop: 'var(--p7-space-4)', padding: 'var(--p7-space-4)' }}>
        <h2 className="p7-eyebrow" style={{ margin: '0 0 var(--p7-space-3)' }}>
          How it was protected
        </h2>
        {ledger.map((d) => (
          <div key={d.id || d.title}
               style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '6px 0' }}>
            <span aria-hidden="true"
                  style={{ flex: 'none', width: '16px', height: '16px', borderRadius: '50%',
                           display: 'grid', placeItems: 'center', marginTop: '3px',
                           fontSize: '9px', fontWeight: 700,
                           background: d.tone === 'hold' ? 'var(--p7-hold-bg)' : 'var(--p7-allow-bg)',
                           color: d.tone === 'hold' ? 'var(--p7-hold-ink)' : 'var(--p7-allow-ink)' }}>
              {d.tone === 'hold' ? '!' : '✓'}
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', font: 'var(--p7-text-ui)',
                             fontFamily: 'var(--p7-font-sans)', fontWeight: 600 }}>{d.title}</span>
              {d.text ? (
                <span style={{ display: 'block', font: 'var(--p7-text-caption)',
                               fontFamily: 'var(--p7-font-sans)',
                               color: 'var(--p7-ink-subtle)' }}>{d.text}</span>
              ) : null}
            </span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-3)',
                    margin: 'var(--p7-space-6) 0 var(--p7-space-3)' }}>
        <h2 className="p7-eyebrow" style={{ margin: 0 }}>What happens on every call</h2>
        <span className="p7-status p7-status--allow" style={{ marginLeft: 'auto' }}>
          Under 50 ms · fails closed
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))',
                    gap: '8px' }}>
        {SEVEN.map(([n, s], i) => (
          <div key={n} className="p7-card"
               style={{ padding: '11px 10px', textAlign: 'center' }}>
            <span aria-hidden="true"
                  style={{ display: 'grid', placeItems: 'center', width: '20px', height: '20px',
                           margin: '0 auto 7px', borderRadius: '50%',
                           background: 'var(--p7-allow-bg)', color: 'var(--p7-allow-ink)',
                           font: '600 9px/1 var(--p7-font-mono, monospace)' }}>{i + 1}</span>
            <span style={{ display: 'block', font: '600 11.5px/16px var(--p7-font-sans)' }}>{n}</span>
            <span className="p7-mono" style={{ display: 'block', fontSize: '10.5px',
                                               color: 'var(--p7-ink-subtle)' }}>{s}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: '12px', marginTop: 'var(--p7-space-5)' }}>
        <div className="p7-card" style={{ padding: 'var(--p7-space-4)',
                                          borderColor: 'var(--p7-accent)' }}>
          <h3 style={{ margin: 0, font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                       fontWeight: 600 }}>
            Point your agent here <span className="p7-status p7-status--running">Recommended</span>
          </h3>
          <p style={{ margin: '2px 0 var(--p7-space-3)', font: 'var(--p7-text-caption)',
                      fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
            One connector. Everything routes through Prove7.
          </p>
          {endpoint ? (
            <div className="p7-mono" style={{ padding: '8px 11px',
                                              border: '1px solid var(--p7-line)',
                                              borderRadius: 'var(--p7-radius-control)' }}>
              {endpoint}
            </div>
          ) : null}
          {credential ? (
            <div className="p7-mono" style={{ marginTop: '6px', padding: '8px 11px',
                                              border: '1px solid var(--p7-line)',
                                              borderRadius: 'var(--p7-radius-control)',
                                              color: 'var(--p7-ink-subtle)' }}>
              {credential}
            </div>
          ) : null}
        </div>
        <div className="p7-card" style={{ padding: 'var(--p7-space-4)' }}>
          <h3 style={{ margin: 0, font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                       fontWeight: 600 }}>Or use it here</h3>
          <p style={{ margin: '2px 0 var(--p7-space-3)', font: 'var(--p7-text-caption)',
                      fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
            Same identity, same rules, same record.
          </p>
          <button type="button" className="p7-btn" onClick={onOpen}>Open it →</button>
        </div>
      </div>
    </div>
  );
}
